import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  DashboardView,
  dashboardViewQuery,
  saveDashboardView,
} from '../../api/command-dashboard-api';
import * as analytics from '../../shared/analytics/analytics.json';
import { useAnalytics } from '../../shared/analytics/create-events';
import { availableHues } from '../../shared/color-utils';
import GeneralDialog from '../../shared/general-dialog';
import { handleErrors } from '../../utils/errors';
import { Messages } from '../../utils/message-constants';
import { ModuleList } from '../../shared/analytics/module-list';
import { useStore } from '../state/zustand-store';
import { useCommandDashboardTilesStore } from './command-dashboard-tiles-store';
import { DEFAULT_DASHBOARD_VIEW } from './dashboard-view-menu-button';

import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const VIEW_NAME_MAX_LENGTH = 25;

interface SaveDashboardViewDialogProps {
  open: boolean;
  handleClose: () => void;
  dashboardView: DashboardView;
}

/** "Save New Dashboard View" dialog: names and persists the current working tile layout. */
export default function SaveDashboardViewDialog({
  open,
  handleClose,
  dashboardView,
}: SaveDashboardViewDialogProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const tileRows = useCommandDashboardTilesStore((state) => state.tileRows);
  const [viewName, setViewName] = useState('');

  const shade = theme.palette.mode === 'light' ? 300 : 500;
  const colors = availableHues.map(({ name, hue }) => ({
    name,
    color: hue[shade],
  }));
  const [selectedColor, setSelectedColor] = useState(colors[0].name);

  const trimmedName = viewName.trim();
  const viewNameExists = dashboardView.savedViews.some(
    (view) => view.name === trimmedName
  );
  const viewNameIsDefault =
    trimmedName.toLowerCase() === DEFAULT_DASHBOARD_VIEW.toLowerCase();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: DashboardView) => saveDashboardView(data),
    onSuccess: (savedView) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Saved('Dashboard view'),
      });
      queryClient.setQueryData(dashboardViewQuery().queryKey, savedView);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Saving('dashboard view'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.command_dashboard.tile_management.save_dashboard_view,
        {
          module: ModuleList.modules.commandDashboard.name,
          section: ModuleList.modules.commandDashboard.sections.tileManagement,
        }
      );
    },
  });

  return (
    <GeneralDialog
      disableRestoreFocus
      open={open}
      title="Save New Dashboard View"
      handleClose={handleClose}
      dialogContent={
        <Stack
          sx={{
            px: 2,
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
          gap={2}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Enter a name for your new dashboard view. This saves down your
            current tile arrangement. This is specific to you, not domain wide.
          </Typography>
          <TextField
            label="View Name"
            value={viewName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setViewName(e.target.value)
            }
            slotProps={{ htmlInput: { maxLength: VIEW_NAME_MAX_LENGTH } }}
            error={viewNameExists || viewNameIsDefault}
            helperText={
              viewNameExists
                ? 'View name already exists'
                : viewNameIsDefault
                ? 'Default view name is not allowed'
                : `${viewName.length}/${VIEW_NAME_MAX_LENGTH} characters`
            }
            autoFocus
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Select a color for your new dashboard view.
          </Typography>
          <Grid
            container
            columns={8}
            rowSpacing={0.5}
            sx={{ width: '50%', mx: 'auto', p: 1 }}
          >
            {colors.map(({ name, color }) => (
              <Grid size={{ xs: 1, md: 1 }} key={name}>
                <ButtonBase
                  sx={{
                    width: 25,
                    height: 25,
                    backgroundColor: color,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border:
                      selectedColor === name
                        ? `2px solid ${theme.palette.text.primary}`
                        : 'none',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => setSelectedColor(name)}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: () => {
          mutate({
            ...dashboardView,
            activeView: trimmedName,
            savedViews: [
              ...dashboardView.savedViews,
              {
                name: trimmedName,
                createdDate: Date.now(),
                color: selectedColor,
                tileRows,
              },
            ],
          });
        },
        loading: isPending,
        disabled:
          trimmedName.length === 0 || viewNameExists || viewNameIsDefault,
      }}
    />
  );
}
